import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card from "@react/shared/ui/Card"

const formatAmount = (cents, symbol = "€") => {
  const value = (cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  return `${value} ${symbol}`
}

const formatVat = pct => `${(Number(pct) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`

const stripTags = html => {
  const doc = new DOMParser().parseFromString(html || "", "text/html")
  doc.body.querySelectorAll("br").forEach(br => br.replaceWith(" "))
  return (doc.body.textContent || "").replace(/ /g, " ").trim()
}

const HEAD_CELL = "sticky top-0 z-10 bg-slate-50 border-b border-slate-200 py-2.5"
const DESIGNATION_CELL = "whitespace-normal break-words min-w-[220px]"

const statusOf = (service, cumulCents) => {
  if (service.locked) return { label: I18n.t("projects.situations.status.solde"), tone: "neutral" }
  if (service.prev_pct >= 100 && service.prev_cents >= service.init_cents) return { label: I18n.t("projects.situations.status.solde"), tone: "neutral" }
  if (cumulCents >= service.init_cents && service.init_cents > 0) return { label: I18n.t("projects.situations.status.done"), tone: "emerald" }
  if (service.draftPct > 0) return { label: I18n.t("projects.situations.status.progress"), tone: "blue" }
  return { label: I18n.t("projects.situations.status.todo"), tone: "slate" }
}

const StatusBadge = ({ status }) => {
  const toneClass = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue:    "bg-blue-50 text-blue-700 border-blue-100",
    neutral: "bg-slate-100 text-slate-600 border-slate-200",
    slate:   "bg-slate-50 text-slate-600 border-slate-200",
  }[status.tone]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${toneClass}`}>
      {status.label}
    </span>
  )
}

const PercentInput = ({ value, onChange, disabled }) => {
  const [draft, setDraft] = useState(null)

  const commit = () => {
    if (draft === null) return
    const number = Number(draft)
    onChange(Number.isFinite(number) ? number : value)
    setDraft(null)
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type      = "range"
        min       = "0"
        max       = "100"
        value     = {value}
        disabled  = {disabled}
        onChange  = {event => onChange(Number(event.target.value))}
        className = "w-16 h-1 accent-slate-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
      />
      <input
        type      = "number"
        min       = "0"
        max       = "100"
        value     = {draft ?? value}
        disabled  = {disabled}
        onFocus   = {event => { setDraft(String(value)); event.target.select() }}
        onChange  = {event => setDraft(event.target.value)}
        onBlur    = {commit}
        onKeyDown = {event => { if (event.key === "Enter") event.target.blur() }}
        className = "w-14 h-7 px-2 text-[12.5px] text-right border border-slate-200 rounded tabular-nums focus:border-slate-900 focus:ring-0 outline-none disabled:bg-slate-100 disabled:text-slate-400"
      />
      <span className="text-[11px] text-slate-400">%</span>
    </div>
  )
}

const GroupsTable = ({ groups, onServiceChange, onGroupCascade, onToggleService, onToggleGroup, onToggleAll, onPrefillFromProgress, currencySymbol, nextSituationNumber }) => {
  const [collapsed, setCollapsed] = useState({})

  const groupMeta = useMemo(() => {
    const meta = {}
    groups.forEach(group => {
      const initSum = group.services.reduce((acc, service) => acc + service.init_cents, 0)
      const cumulSum = group.services.reduce((acc, service) => acc + Math.round((service.init_cents * service.draftPct) / 100), 0)
      const prevSum = group.services.reduce((acc, service) => acc + service.prev_cents, 0)
      const groupPct = initSum > 0 ? Math.round((cumulSum / initSum) * 100) : 0
      const eligibleServices = group.services.filter(service => !service.locked)
      const selectedCount = eligibleServices.filter(service => service.selected).length
      const vatRates = [...new Set(group.services.map(service => Number(service.vat_pct) || 0))]
      meta[group.id] = {
        initSum, cumulSum, prevSum, groupPct,
        eligibleServices, selectedCount,
        groupVat: vatRates.length === 1 ? vatRates[0] : null,
        allSelected:  eligibleServices.length > 0 && selectedCount === eligibleServices.length,
        someSelected: selectedCount > 0 && selectedCount < eligibleServices.length,
      }
    })
    return meta
  }, [groups])

  const allEligibleSelected = useMemo(() => {
    const eligible = groups.flatMap(group => group.services.filter(service => !service.locked))
    return eligible.length > 0 && eligible.every(service => service.selected)
  }, [groups])

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
            {I18n.t("projects.situations.next_situation_label", { number: nextSituationNumber })}
          </div>
          <h2 className="text-[18px] font-semibold text-slate-900 mt-0.5">
            {I18n.t("projects.situations.table.title")}
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-1 max-w-[60ch]">
            {I18n.t("projects.situations.table.subtitle")}
          </p>
        </div>
        {onPrefillFromProgress && (
          <button
            type      = "button"
            onClick   = {onPrefillFromProgress}
            title     = {I18n.t("projects.situations.prefill_hint")}
            className = "shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12.5px] font-medium text-blue-700 bg-blue-50 ring-1 ring-blue-100 hover:bg-blue-100"
          >
            <FontAwesomeIcon icon="wand-magic-sparkles" className="text-[12px]" />
            {I18n.t("projects.situations.prefill_button")}
          </button>
        )}
      </div>

      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-[13px] whitespace-nowrap">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
              <th className={`${HEAD_CELL} pl-3 pr-1 text-left w-7`}>
                <input
                  type      = "checkbox"
                  checked   = {allEligibleSelected}
                  onChange  = {event => onToggleAll(event.target.checked)}
                  className = "rounded border-slate-300"
                />
              </th>
              <th className={`${HEAD_CELL} px-1 w-6`} aria-hidden="true" />
              <th className={`${HEAD_CELL} px-2 text-left w-12`}>{I18n.t("projects.situations.table.code")}</th>
              <th className={`${HEAD_CELL} px-2 text-left`}>{I18n.t("projects.situations.table.designation")}</th>
              <th className={`${HEAD_CELL} px-3 text-right`}>{I18n.t("projects.situations.table.vat")}</th>
              <th className={`${HEAD_CELL} px-3 text-right`}>{I18n.t("projects.situations.table.initial")}</th>
              <th className={`${HEAD_CELL} px-3 text-left`}>{I18n.t("projects.situations.table.progress")}</th>
              <th className={`${HEAD_CELL} px-3 text-right`}>{I18n.t("projects.situations.table.cumul")}</th>
              <th className={`${HEAD_CELL} px-3 text-right`}>{I18n.t("projects.situations.table.already_billed")}</th>
              <th className={`${HEAD_CELL} px-3 text-right`}>{I18n.t("projects.situations.table.to_bill")}</th>
              <th className={`${HEAD_CELL} px-3 text-left`}>{I18n.t("projects.situations.table.status")}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(group => {
              const meta = groupMeta[group.id]
              const toBill = Math.max(0, meta.cumulSum - meta.prevSum)
              const isCollapsed = !!collapsed[group.id]
              return (
                <React.Fragment key={group.id}>
                  <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                    <td className="pl-3 pr-1 py-2.5">
                      <input
                        type      = "checkbox"
                        checked   = {meta.allSelected}
                        ref       = {el => { if (el) el.indeterminate = meta.someSelected }}
                        onChange  = {event => onToggleGroup(group.id, event.target.checked)}
                        className = "rounded border-slate-300"
                      />
                    </td>
                    <td className="px-1 py-2.5 text-center">
                      <button
                        type      = "button"
                        onClick   = {() => setCollapsed(c => ({ ...c, [group.id]: !c[group.id] }))}
                        className = "text-slate-400 hover:text-slate-700"
                      >
                        <FontAwesomeIcon icon={isCollapsed ? "chevron-right" : "chevron-down"} className="text-[10px]" />
                      </button>
                    </td>
                    <td className="px-2 py-2.5 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold tabular-nums">{group.number}</td>
                    <td className={`px-2 py-2.5 ${DESIGNATION_CELL}`}>{stripTags(group.label) || I18n.t("projects.situations.table.lot_fallback")}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{meta.groupVat != null ? formatVat(meta.groupVat) : "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatAmount(meta.initSum, currencySymbol)}</td>
                    <td className="px-3 py-2.5">
                      <PercentInput
                        value    = {meta.groupPct}
                        onChange = {next => onGroupCascade(group.id, next)}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatAmount(meta.cumulSum, currencySymbol)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{formatAmount(meta.prevSum, currencySymbol)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatAmount(toBill, currencySymbol)}</td>
                    <td className="px-3 py-2.5" />
                  </tr>
                  {!isCollapsed && group.services.map(service => {
                    const cumulCents = Math.round((service.init_cents * service.draftPct) / 100)
                    const toBillCents = Math.max(0, cumulCents - service.prev_cents)
                    const status = statusOf(service, cumulCents)
                    return (
                      <tr key={service.id} className={`border-t border-slate-100 ${service.locked ? "text-slate-400" : ""}`}>
                        <td className="pl-6 pr-1 py-2">
                          <input
                            type      = "checkbox"
                            checked   = {service.selected}
                            disabled  = {service.locked || toBillCents <= 0}
                            onChange  = {event => onToggleService(group.id, service.id, event.target.checked)}
                            className = "rounded border-slate-300"
                          />
                        </td>
                        <td className="px-1 py-2" />
                        <td className="px-2 py-2 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold tabular-nums">{service.code}</td>
                        <td className={`px-2 py-2 ${DESIGNATION_CELL}`}>
                          <span className={service.locked ? "" : "text-slate-700"}>{stripTags(service.name)}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatVat(service.vat_pct)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatAmount(service.init_cents, currencySymbol)}</td>
                        <td className="px-3 py-2">
                          <PercentInput
                            value    = {service.draftPct}
                            disabled = {service.locked}
                            onChange = {next => onServiceChange(group.id, service.id, { draftPct: next })}
                          />
                          {service.task_pct != null && !service.locked && Math.round(service.task_pct) !== service.draftPct && (
                            <button
                              type      = "button"
                              onClick   = {() => onServiceChange(group.id, service.id, { draftPct: service.task_pct })}
                              title     = {I18n.t("projects.situations.prefill_hint")}
                              className = "mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 tabular-nums"
                            >
                              <FontAwesomeIcon icon="hard-hat" className="text-[9px]" />
                              {I18n.t("projects.situations.task_progress_pill", { pct: Math.round(service.task_pct) })}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatAmount(cumulCents, currencySymbol)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{formatAmount(service.prev_cents, currencySymbol)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatAmount(toBillCents, currencySymbol)}</td>
                        <td className="px-3 py-2"><StatusBadge status={status} /></td>
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

export default GroupsTable
